import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  UsersIcon,
  ClockIcon,
  FunnelIcon,
  TableCellsIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarDaysIcon,
  Squares2X2Icon,
  CogIcon
} from '@heroicons/react/24/outline';
import { Doughnut } from 'react-chartjs-2';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Dropdown from '../components/ui/Dropdown';
import MultiSelect from '../components/ui/MultiSelect';
import Input from '../components/ui/Input';
import EmployeeService from '../services/employee.service';
import ReportsService from '../services/reports.service';
import { useHeaderSearch } from '../contexts/SearchContext';
import toast from '../utils/toast';
import { buildReportTableData } from '../utils/reportTableData';
import { formatDateDisplay } from '../utils/dateDisplay';

const Reports = () => {
  const { headerSearch } = useHeaderSearch();
  const [reportData, setReportData] = useState({
    attendance: { overallStats: {}, records: [] },
    leaves: { overallStats: {}, records: [] },
    employees: []
  });
  const [dashboardStats, setDashboardStats] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('month');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeStatus, setEmployeeStatus] = useState('active');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const [viewMode, setViewMode] = useState('charts'); // 'charts' or 'table'
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const hasFetchedOnceRef = useRef(false);
  const reportRequestRef = useRef(0);

  const dateRangeOptions = [
    { 
      value: 'week', 
      label: 'This Week',
      description: 'Last 7 days',
      icon: CalendarDaysIcon
    },
    { 
      value: 'month', 
      label: 'This Month',
      description: 'Current month data',
      icon: CalendarIcon
    },
    { 
      value: 'quarter', 
      label: 'This Quarter',
      description: 'Last 3 months',
      icon: Squares2X2Icon
    },
    { 
      value: 'year', 
      label: 'This Year',
      description: 'Current year data',
      icon: ChartBarIcon
    },
    { 
      value: 'custom', 
      label: 'Custom Range',
      description: 'Select specific dates',
      icon: CogIcon
    }
  ];

  const isCustomRangeIncomplete =
    dateRange === 'custom' && (!customDateRange.startDate || !customDateRange.endDate);

  const reportParams = useMemo(() => {
    const params = { employeeStatus };
    if (dateRange !== 'custom') {
      params.range = dateRange;
    } else if (customDateRange.startDate && customDateRange.endDate) {
      params.startDate = customDateRange.startDate;
      params.endDate = customDateRange.endDate;
    }
    if (selectedEmployees.length > 0) {
      params.email_address = selectedEmployees.join(',');
    }
    return params;
  }, [dateRange, selectedEmployees, employeeStatus, customDateRange.startDate, customDateRange.endDate]);

  const fetchReportData = useCallback(async ({ showInitialLoader = false } = {}) => {
    if (isCustomRangeIncomplete) return;
    const requestId = ++reportRequestRef.current;

    try {
      if (showInitialLoader || !hasFetchedOnceRef.current) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }

      const [attendanceRes, leavesRes, employeesRes, statsRes] = await Promise.all([
        ReportsService.getAttendanceReport(reportParams).catch((err) => {
          console.error('Attendance API error:', err);
          return { overallStats: {}, records: [] };
        }),
        ReportsService.getLeaveReport(reportParams).catch((err) => {
          console.error('Leaves API error:', err);
          return { overallStats: {}, records: [] };
        }),
        EmployeeService.getAll().catch((err) => {
          console.error('Employees API error:', err);
          return [];
        }),
        ReportsService.getStats(reportParams).catch((err) => {
          console.error('Stats API error:', err);
          return {};
        })
      ]);

      if (requestId !== reportRequestRef.current) return;
      const normalizedEmployees = EmployeeService.normalizeEmployeeList(employeesRes);

      setReportData({
        attendance: attendanceRes || { overallStats: {}, records: [] },
        leaves: leavesRes || { overallStats: {}, records: [] },
        employees: normalizedEmployees
      });

      setDashboardStats(statsRes || {});
      hasFetchedOnceRef.current = true;
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to load report data');
    } finally {
      if (requestId === reportRequestRef.current) {
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  }, [isCustomRangeIncomplete, reportParams]);

  useEffect(() => {
    reportRequestRef.current += 1;
    setCurrentPage(1);
    if (!hasFetchedOnceRef.current) {
      fetchReportData({ showInitialLoader: true });
      return;
    }

    if (isCustomRangeIncomplete) return;

    const timeoutId = setTimeout(() => {
      fetchReportData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [fetchReportData, isCustomRangeIncomplete]);

  const generateReport = async (type) => {
    try {
      if (isCustomRangeIncomplete) {
        toast.error('Please select both start and end dates for the custom range');
        return;
      }

      const reportBlob = await ReportsService.generateReport(type, reportParams);
      
      const url = window.URL.createObjectURL(new Blob([reportBlob], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      const fileName = selectedEmployees.length > 0 
        ? `${type}-report-filtered-${dateRange}.xlsx`
        : `${type}-report-${dateRange}.xlsx`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type} Excel report downloaded successfully`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };



  const clearFilters = () => {
    setRefreshing(false);
    setSelectedEmployees([]);
    setEmployeeStatus('active');
    setCustomDateRange({ startDate: '', endDate: '' });
    setDateRange('month');
    setCurrentPage(1);
    setSortConfig({ key: 'date', direction: 'asc' });
    // fetchReportData will be called automatically by useEffect
  };



  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const employeeOptions = useMemo(() =>
    (Array.isArray(reportData.employees) ? reportData.employees : [])
      .filter(emp => employeeStatus === 'all' || emp.is_active === (employeeStatus === 'active')).map((emp) => ({
      value: emp.email_address,
      label: `${emp.first_name} ${emp.last_name}`,
      badge: emp.empcode,
      description: emp.designation,
      searchText: `${emp.first_name} ${emp.last_name} ${emp.empcode} ${emp.designation || ''}`
    })), [reportData.employees, employeeStatus]);

  const tableData = useMemo(
    () => buildReportTableData(reportData.attendance, reportData.leaves),
    [reportData.attendance, reportData.leaves]
  );

  const searchFilteredTableData = useMemo(() => (
    headerSearch
      ? tableData.filter(row => {
        const q = headerSearch.toLowerCase();
        return Object.values(row).some(val =>
          val != null && String(val).toLowerCase().includes(q)
        );
      })
      : tableData
  ), [headerSearch, tableData]);

  const sortedTableData = useMemo(() => [...searchFilteredTableData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const sortKey = sortConfig.key === 'date' ? 'dateKey' : sortConfig.key;
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    
    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  }), [searchFilteredTableData, sortConfig]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(
    () => sortedTableData.slice(indexOfFirstItem, indexOfLastItem),
    [indexOfFirstItem, indexOfLastItem, sortedTableData]
  );
  const totalPages = Math.ceil(sortedTableData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Calculate real attendance chart data using dashboard stats or fallback to report data
  const attendanceChartData = {
    labels: ['Present', 'Absent', 'Leave', 'Half Day'],
    datasets: [{
      data: [
        dashboardStats.breakdown?.attendance?.present ?? reportData.attendance?.overallStats?.totalPresent ?? 0,
        dashboardStats.breakdown?.attendance?.absent ?? reportData.attendance?.overallStats?.totalAbsent ?? 0,
        dashboardStats.breakdown?.attendance?.leave ?? reportData.attendance?.overallStats?.totalLeave ?? 0,
        dashboardStats.breakdown?.attendance?.halfDay ?? reportData.attendance?.overallStats?.totalHalfDay ?? 0
      ],
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(245, 158, 11, 0.8)',
        'rgba(59, 130, 246, 0.8)'
      ],
      borderWidth: 0,
    }]
  };

  // Calculate real leaves trend data (by status) using dashboard stats or fallback to report data
  const leavesTrendData = {
    labels: ['Approved', 'Pending', 'Rejected', 'Cancelled'],
    datasets: [{
      label: 'Leave Status',
      data: [
        dashboardStats.breakdown?.leaves?.approved ?? reportData.leaves?.overallStats?.totalApproved ?? 0,
        dashboardStats.breakdown?.leaves?.pending ?? reportData.leaves?.overallStats?.totalPending ?? 0,
        dashboardStats.breakdown?.leaves?.rejected ?? reportData.leaves?.overallStats?.totalRejected ?? 0,
        dashboardStats.breakdown?.leaves?.cancelled ?? reportData.leaves?.overallStats?.totalCancelled ?? 0
      ],
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)', // Green for approved
        'rgba(245, 158, 11, 0.8)',  // Yellow for pending
        'rgba(239, 68, 68, 0.8)',   // Red for rejected
        'rgba(107, 114, 128, 0.8)'  // Gray for cancelled
      ],
      borderWidth: 0,
    }]
  };

  const reportTypes = [
    { id: 'attendance', name: 'Attendance Report', icon: ClockIcon, description: 'Employee attendance summary' },
    { id: 'leaves', name: 'Leave Report', icon: CalendarIcon, description: 'Leave requests and approvals' },
    { id: 'employees', name: 'Employee Report', icon: UsersIcon, description: 'Employee details and statistics' }
  ];

  // Calculate real quick stats using dashboard stats API
  const quickStats = [
    { 
      name: 'Total Employees', 
      value: dashboardStats.totalEmployees ?? employeeOptions.length,
      icon: UsersIcon, 
      color: 'text-blue-600', 
      bgColor: 'bg-blue-100' 
    },
    { 
      name: 'Attendance Rate', 
      value: dashboardStats.attendanceRate || '0%',
      icon: ClockIcon, 
      color: 'text-green-600', 
      bgColor: 'bg-green-100' 
    },
    { 
      name: 'Pending Leaves', 
      value: dashboardStats.pendingLeaves ?? reportData.leaves?.overallStats?.totalPending ?? 0,
      icon: CalendarIcon, 
      color: 'text-yellow-600', 
      bgColor: 'bg-yellow-100' 
    },
    { 
      name: 'Total Records', 
      value: dashboardStats.totalRecords ?? ((reportData.attendance?.overallStats?.totalRecords || 0) + (reportData.leaves?.overallStats?.totalRecords || 0)),
      icon: ChartBarIcon, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-100' 
    }
  ];

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-xl mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 rounded-xl p-6 text-white"
      >
        <h1 className="text-3xl font-bold mb-2">Reports & Analytics</h1>
        <p className="text-slate-200">
          Generate and download comprehensive reports
          {dashboardStats.dateRange && (
            <span className="ml-2 text-slate-300">
              • {formatDateDisplay(dashboardStats.dateRange.startDate)} to {formatDateDisplay(dashboardStats.dateRange.endDate)}
            </span>
          )}
        </p>
      </motion.div>

      {/* Compact Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-gray-600 min-h-[36px]">
            <FunnelIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          
          {/* Date Range Dropdown */}
          <div className="w-full sm:w-[220px]">
            <Dropdown
              value={dateRange}
              onChange={setDateRange}
              options={dateRangeOptions}
              placeholder="Select Date Range"
              icon={CalendarIcon}
              size="sm"
              className="text-sm"
            />
          </div>

          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <>
              <div className="w-full sm:w-[170px]">
                <Input
                  type="date"
                  value={customDateRange.startDate}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  placeholder="Start Date"
                  size="sm"
                  className="text-sm"
                />
              </div>
              <div className="w-full sm:w-[170px]">
                <Input
                  type="date"
                  value={customDateRange.endDate}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  placeholder="End Date"
                  size="sm"
                  className="text-sm"
                />
              </div>
            </>
          )}

          <div className="w-full sm:w-[220px]">
            <Dropdown
              value={employeeStatus}
              onChange={(value) => {
                setEmployeeStatus(value);
                setSelectedEmployees([]);
                setCurrentPage(1);
              }}
              options={[
                { value: 'active', label: 'Active employees' },
                { value: 'inactive', label: 'Inactive employees' },
                { value: 'all', label: 'All employees' },
              ]}
              placeholder="Employee Status"
              icon={UsersIcon}
              size="sm"
              className="text-sm"
            />
          </div>

          {/* Employee Multi-Select */}
          <div className="w-full lg:min-w-[260px] lg:max-w-[360px]">
            <MultiSelect
              value={selectedEmployees}
              onChange={setSelectedEmployees}
              options={employeeOptions}
              placeholder="All matching employees"
              size="sm"
              searchable={true}
              className="text-sm"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex w-full items-center gap-1 rounded-lg bg-gray-100 p-1 sm:w-auto">
            <button
              onClick={() => setViewMode('charts')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'charts'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ChartBarIcon className="w-4 h-4 inline mr-1" />
              Charts
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TableCellsIcon className="w-4 h-4 inline mr-1" />
              Table
            </button>
          </div>

          {/* Apply & Clear Buttons */}
          <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
            <Button
              variant="primary"
              size="sm"
              onClick={() => fetchReportData()}
              loading={refreshing}
              disabled={isCustomRangeIncomplete}
              className="w-full shadow-sm sm:w-auto"
            >
              Apply Filters
            </Button>
            
            {(selectedEmployees.length > 0 || dateRange === 'custom' || employeeStatus !== 'active') && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                disabled={refreshing}
                className="w-full sm:w-auto"
              >
                Reset
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 flex min-h-[20px] items-center justify-between text-xs text-gray-500">
          <span>
            {isCustomRangeIncomplete
              ? 'Select both custom dates to refresh the report.'
              : 'Filters refresh automatically after a short pause.'}
          </span>
          {refreshing && (
            <span className="inline-flex items-center gap-2 font-medium text-blue-600">
              <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
              Refreshing report data...
            </span>
          )}
        </div>


      </div>

      {employeeStatus !== 'active' && reportData.employees.some(employee =>
        employee.is_active === false && !employee.leaving_date &&
        (selectedEmployees.length === 0 || selectedEmployees.includes(employee.email_address))
      ) && (
        <p className="text-sm text-amber-700" role="status">
          Some inactive employees have no last working date. Their existing attendance is included;
          update their leaving date in Employees to exclude days after departure.
        </p>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <Card.Body className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Charts Section */}
      {viewMode === 'charts' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Attendance Overview */}
          <Card>
            <Card.Header>
              <h3 className="text-lg font-semibold">Attendance Overview</h3>
              <p className="text-sm text-gray-600">Current {dateRange} breakdown</p>
            </Card.Header>
            <Card.Body>
              <div className="h-80">
                {((dashboardStats.breakdown?.attendance?.total ?? reportData.attendance?.overallStats?.totalRecords ?? 0) > 0) ? (
                  <Doughnut
                    data={attendanceChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                              return `${label}: ${value} (${percentage}%)`;
                            }
                          }
                        }
                      },
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <ChartBarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>No attendance data available</p>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Leave Status Overview */}
          <Card>
            <Card.Header>
              <h3 className="text-lg font-semibold">Leave Status Overview</h3>
              <p className="text-sm text-gray-600">Current {dateRange} leave breakdown</p>
            </Card.Header>
            <Card.Body>
              <div className="h-80">
                {((dashboardStats.breakdown?.leaves?.total ?? reportData.leaves?.overallStats?.totalRecords ?? 0) > 0) ? (
                  <Doughnut
                    data={leavesTrendData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                              return `${label}: ${value} (${percentage}%)`;
                            }
                          }
                        }
                      },
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>No leave data available</p>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Table Section */}
      {viewMode === 'table' && (
        <>
          {/* Table Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TableCellsIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Total Records</p>
                  <p className="text-lg font-semibold text-gray-900">{sortedTableData.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ClockIcon className="w-5 h-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Attendance Records</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {sortedTableData.filter(item => item.type === 'Attendance').length}
                  </p>
                  <p className="text-xs text-gray-500">
                    Present: {sortedTableData.filter(item => item.type === 'Attendance' && item.status === 'Present').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <CalendarIcon className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Leave Records</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {sortedTableData.filter(item => item.type === 'Leave' || item.leaveStatus === 'APPROVED').length}
                  </p>
                  <p className="text-xs text-gray-500">
                    Pending: {sortedTableData.filter(item => item.type === 'Leave' && item.status === 'PENDING').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <UsersIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Unique Employees</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Set(sortedTableData.map(item => item.employeeEmail || item.employeeCode)).size}
                  </p>
                  <p className="text-xs text-gray-500">
                    In filtered data
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Detailed Report Table</h3>
                  <p className="text-sm text-gray-600">
                    Showing {currentItems.length} of {sortedTableData.length} records
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <EyeIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
              </div>
            </Card.Header>
          <Card.Body className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('type')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Type
                        {sortConfig.key === 'type' && (
                          sortConfig.direction === 'asc' ? 
                            <ArrowUpIcon className="w-3 h-3" /> : 
                            <ArrowDownIcon className="w-3 h-3" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('employeeName')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Employee
                        {sortConfig.key === 'employeeName' && (
                          sortConfig.direction === 'asc' ? 
                            <ArrowUpIcon className="w-3 h-3" /> : 
                            <ArrowDownIcon className="w-3 h-3" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('date')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Date
                        {sortConfig.key === 'date' && (
                          sortConfig.direction === 'asc' ? 
                            <ArrowUpIcon className="w-3 h-3" /> : 
                            <ArrowDownIcon className="w-3 h-3" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Status
                        {sortConfig.key === 'status' && (
                          sortConfig.direction === 'asc' ? 
                            <ArrowUpIcon className="w-3 h-3" /> : 
                            <ArrowDownIcon className="w-3 h-3" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      In Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Out Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Work Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Break Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Overtime
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.length > 0 ? (
                    currentItems.map((row, index) => (
                      <tr key={row.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            row.type === 'Attendance' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {row.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{row.employeeName}</div>
                          <div className="text-sm text-gray-500">{row.employeeCode}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span title={row.statusLabel || row.status} className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            row.status === 'Present' || row.status === 'APPROVED' || row.leaveStatus === 'APPROVED'
                              ? 'bg-green-100 text-green-800'
                              : row.status === 'Absent' || row.status === 'REJECTED'
                              ? 'bg-red-100 text-red-800'
                              : row.status === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {row.status}
                          </span>
                          {row.workMode === 'WFH' && <span className="ml-2 text-xs text-blue-700">WFH</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.inTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.outTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.workTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.breakTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.overtime}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          <span title={row.details}>{row.details}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <TableCellsIcon className="w-12 h-12 text-gray-300 mb-4" />
                          <p className="text-lg font-medium">No data available</p>
                          <p className="text-sm">Try adjusting your filters or date range</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedTableData.length)} of {sortedTableData.length} results
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      const pageNumber = currentPage <= 3 
                        ? index + 1 
                        : currentPage + index - 2;
                      
                      if (pageNumber > totalPages) return null;
                      
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => paginate(pageNumber)}
                          className={`px-3 py-1 text-sm rounded-md transition-colors ${
                            currentPage === pageNumber
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
        </>
      )}

      {/* Report Generation */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-semibold">Generate Reports</h3>
          <p className="text-sm text-gray-600">Download detailed reports in Excel format</p>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reportTypes.map((report, index) => {
              const Icon = report.icon;
              return (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-300"
                >
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{report.name}</h4>
                    <p className="text-sm text-gray-600 mb-4">{report.description}</p>
                    <Button
                      variant="outline"
                      onClick={() => generateReport(report.id)}
                      className="w-full flex items-center justify-center space-x-2"
                    >
                      <DocumentArrowDownIcon className="w-4 h-4" />
                      <span>Download</span>
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Reports;
